import { Button, DrawerCard, DrawerField, DrawerTitle, Input, Select, Toggle } from "@mixitone/components";
import TrophyIcon from "@mixitone/icons/trophy.svg";
import { TournamentCompetition, TournamentEntry, TournamentPlayer } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { List, setupModelRelations } from "@mixitone/oom";
import { isPresent } from "@mixitone/util";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import { injectModal } from "lib/injectParams";
import { TournamentEntrySearch } from "components/Forms/TournamentEntrySearch";
import routeService from "services/RouteService";
import { messagesService } from "services";

interface Props {
  id: string;
  competitionId: string;
}

interface WinnerSelection {
  entry: TournamentEntry | null;
  metadata: Record<string, any>;
}

interface State {
  winners: WinnerSelection[];
  allEntries: List<TournamentEntry>;
  saving: boolean;
  competitionId?: string;
  markAsCompleted: boolean;
  competition?: TournamentCompetition;
}

class WinnerSelectorController extends ApplicationController<State, Props> {
  get initialState(): State {
    return {
      winners: [],
      allEntries: new List(),
      saving: false,
      competitionId: undefined,
      markAsCompleted: false,
      competition: undefined,
    };
  }

  async initialize(props: Props) {
    await this.changeProps(props);
  }

  async changeProps(props: Props) {
    if (!props?.competitionId) {
      console.error("WinnerSelector: competitionId is missing from props", props);
      return;
    }

    this.state.competitionId = props.competitionId;

    const competition = await TournamentCompetition.query().find(props.competitionId);
    if (!competition) return;

    this.state.competition = competition;

    // Load all entries for selection first
    const allEntries = await TournamentEntry.query()
      .eq("tournament_competition_id", props.competitionId)
      .preload("registrations", "competition")
      .all();
    const allRegistrations = allEntries.flatMap((e) => e.registrations);
    const allPlayers = await TournamentPlayer.query()
      .in("id", allRegistrations.map((r) => r.tournament_player_id).filter(isPresent))
      .all();
    setupModelRelations({ entries: allEntries, registrations: allRegistrations, players: allPlayers });

    this.state.allEntries = new List(...allEntries);

    // Load existing winners if any
    const existingWinners: WinnerSelection[] = [];
    if (competition.format_data?.winners && Array.isArray(competition.format_data.winners)) {
      const entryIds = competition.format_data.winners.map((w: [string, any]) => w[0]);
      const winnerEntries = allEntries.filter((e) => entryIds.includes(e.id!));

      competition.format_data.winners.forEach((winner: [string, any], index: number) => {
        const entry = winnerEntries.find((e) => e.id === winner[0]);
        if (entry) {
          existingWinners[index] = {
            entry,
            metadata: winner[1] || {},
          };
        }
      });
    }

    this.state.winners = existingWinners.length > 0 ? existingWinners : [{ entry: null, metadata: {} }];
  }

  actionAddWinner() {
    this.state.winners.push({ entry: null, metadata: {} });
  }

  actionRemoveWinner(index: number) {
    this.state.winners.splice(index, 1);
  }

  actionSetWinnerEntry(index: number, entry: TournamentEntry | null) {
    if (!this.state.winners[index]) {
      this.state.winners[index] = { entry, metadata: {} };
    } else {
      this.state.winners[index].entry = entry;
    }
  }

  actionSetWinnerMetadata(index: number, key: string, value: string) {
    if (!this.state.winners[index]) return;
    // Store division as number, other fields as string
    const processedValue = key === "division" && value ? Number.parseInt(value, 10) : value || undefined;
    this.state.winners[index].metadata = {
      ...this.state.winners[index].metadata,
      [key]: processedValue,
    };
  }

  async actionSaveWinners() {
    if (this.state.saving) return;

    const validWinners = this.state.winners.filter((w) => w.entry !== null);
    if (validWinners.length === 0) {
      messagesService.addMessage("Please select at least one winner", "error");
      return;
    }

    const competitionId = this.state.competitionId;
    if (!competitionId) {
      messagesService.addMessage("Competition ID is missing", "error");
      return;
    }

    this.state.saving = true;
    try {
      const competition = await TournamentCompetition.query().find(competitionId);
      if (!competition) {
        messagesService.addMessage("Competition not found", "error");
        return;
      }

      const winners: Array<[string, Record<string, any>]> = validWinners.map((w) => [
        w.entry!.id!,
        w.metadata,
      ]);

      const formatData = competition.format_data || {};
      formatData.winners = winners;
      competition.format_data = formatData;
      await competition.save();

      // Mark competition as completed if checkbox is checked
      if (this.state.markAsCompleted && !competition.isCompleted) {
        await competition.complete();
      }

      routeService.closeModal();
      messagesService.addMessage("Winners saved", "success");
    } catch (error) {
      messagesService.addMessage(`Error saving winners: ${error}`, "error");
    } finally {
      this.state.saving = false;
    }
  }
}

export const WinnerSelector = ApplicationView<Props>(() => {
  const controller = WinnerSelectorController.use();
  const { winners, allEntries, saving, markAsCompleted, competition } = controller.state;
  const hasDivisions = competition?.hasDivisions() ?? false;
  const numDivisions = competition?.getNumDivisions();

  return (
    <RoutedDrawer>
      <DrawerTitle icon={<TrophyIcon className="mr-2 w-[24px] fill-gray-400" />}>Select winners</DrawerTitle>
      <div className="space-y-6">
        <DrawerCard>
          <div className="space-y-4">
            {winners.map((winner, index) => (
              <div key={index} className="p-4 space-y-2 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Winner {index + 1}</h4>
                  {winners.length > 1 && (
                    <Button
                      kind="plain"
                      className="text-red-500"
                      onClick={() => controller.actionRemoveWinner(index)}
                      disabled={saving}
                    >
                      Remove
                    </Button>
                  )}
                </div>
                <DrawerField label="Entry" className="relative z-10">
                  {winner.entry ? (
                    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <span className="font-medium">{winner.entry.name || "Unnamed entry"}</span>
                      <Button
                        kind="plain"
                        className="text-red-500"
                        onClick={() => controller.actionSetWinnerEntry(index, null)}
                        disabled={saving}
                      >
                        Clear
                      </Button>
                    </div>
                  ) : (
                    <TournamentEntrySearch
                      entries={allEntries}
                      value={null}
                      onSelect={(entry) => controller.actionSetWinnerEntry(index, entry)}
                      placeholder="Select winner"
                      disabled={saving}
                    />
                  )}
                </DrawerField>
                {hasDivisions && numDivisions && (
                  <DrawerField label="Division">
                    <Select
                      value={winner.metadata.division?.toString() || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        controller.actionSetWinnerMetadata(
                          index,
                          "division",
                          value ? Number.parseInt(value, 10).toString() : "",
                        );
                      }}
                      disabled={saving}
                    >
                      <option value="">Select division</option>
                      {Array.from({ length: numDivisions }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n.toString()}>
                          Division {n}
                        </option>
                      ))}
                    </Select>
                  </DrawerField>
                )}
                <DrawerField label="Place (optional)">
                  <Input
                    type="text"
                    value={winner.metadata.place || ""}
                    onChange={(e) => controller.actionSetWinnerMetadata(index, "place", e.target.value)}
                    placeholder="e.g., 1st Place"
                    disabled={saving}
                  />
                </DrawerField>
                <DrawerField label="Notes (optional)">
                  <Input
                    type="text"
                    value={winner.metadata.notes || ""}
                    onChange={(e) => controller.actionSetWinnerMetadata(index, "notes", e.target.value)}
                    placeholder="Additional notes"
                    disabled={saving}
                  />
                </DrawerField>
              </div>
            ))}
            <Button kind="secondary" onClick={() => controller.actionAddWinner()} disabled={saving}>
              Add Another Winner
            </Button>
          </div>
        </DrawerCard>
        <DrawerCard>
          <DrawerField label="Competition is finished" labelWidth="wide">
            <Toggle
              checked={markAsCompleted}
              onChange={(e) => {
                controller.state.markAsCompleted = e.target.checked;
              }}
              disabled={saving}
            />
          </DrawerField>
        </DrawerCard>
        <DrawerCard>
          <div className="flex justify-end gap-2">
            <Button kind="secondary" onClick={() => routeService.closeModal()} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => controller.actionSaveWinners()} disabled={saving} spinner={saving}>
              Save winners
            </Button>
          </div>
        </DrawerCard>
      </div>
    </RoutedDrawer>
  );
});

export { WinnerSelectorController };
export default injectModal(WinnerSelectorController.scope(WinnerSelector));
